// The app's pages, once: the sidebar, the phone tabs, the More page and back links all read this list.
//
// "Where did I come from?" for back links. A link into a trade (or a journal day) carries the page it was
// opened from in the router's location state, including that page's own state, so backing out keeps
// working through several levels: Calendar → journal day → trade → journal day → Calendar.

import type { JSX } from 'react';
import { useLocation, type Location } from 'react-router-dom';
import { Icon } from './components/Icons.tsx';
import { dateLabel } from './format.ts';

export type NavItem = {
  to: string;
  label: string;
  icon: () => JSX.Element;
  /** Main pages sit in the sidebar's list; account pages sit at its foot. */
  place: 'main' | 'foot';
  /** On phones: a bottom tab, or a tile on the More page. */
  tab?: boolean;
  /** One line for the More page. */
  sub?: string;
  /** Carries the journaling count badge (Journal, where that work is done). */
  badge?: boolean;
  admin?: boolean;
};

export const NAV: NavItem[] = [
  { to: '/', label: 'Today', icon: Icon.today, place: 'main', tab: true },
  { to: '/calendar', label: 'Calendar', icon: Icon.calendar, place: 'main', tab: true },
  { to: '/journal', label: 'Journal', icon: Icon.journal, place: 'main', tab: true, badge: true },
  { to: '/trades', label: 'Trades', icon: Icon.trades, place: 'main', sub: 'Every trade, sortable, with CSV export' },
  { to: '/performance', label: 'Performance', icon: Icon.reports, place: 'main', tab: true, sub: 'Your numbers, breakdowns and discipline' },
  { to: '/market', label: 'Market', icon: Icon.market, place: 'main', sub: 'Sentiment, movers, catalysts and how runs play out' },
  { to: '/practice', label: 'Practice', icon: Icon.practice, place: 'main', sub: 'A market simulator: Level 2, the tape and hotkeys' },
  { to: '/playbook', label: 'Playbook', icon: Icon.playbook, place: 'main', sub: 'Your setups, rules and examples' },
  { to: '/import', label: 'Import', icon: Icon.import, place: 'foot', sub: 'TradeZero, IBKR, thinkorswim, Webull, DAS' },
  { to: '/settings', label: 'Settings', icon: Icon.settings, place: 'foot', sub: 'Face ID, appearance, chart data, account' },
  { to: '/start', label: 'Getting started', icon: Icon.start, place: 'foot', sub: 'A quick guide to the main features' },
  { to: '/admin', label: 'Admin', icon: Icon.admin, place: 'foot', sub: 'Sign-up, invites and accounts', admin: true },
];

export const navFor = (isAdmin: boolean) => NAV.filter((n) => !n.admin || isAdmin);

/** Is this nav item the current page (including its sub-pages, like /playbook/Setup)? */
export const isCurrent = (item: NavItem, pathname: string) =>
  item.to === '/' ? pathname === '/' : pathname === item.to || pathname.startsWith(`${item.to}/`);

export type From = { path: string; label: string; state?: unknown };

function labelFor(pathname: string): string {
  if (pathname.startsWith('/day/')) return dateLabel(pathname.slice('/day/'.length), { weekday: 'short', month: 'short', day: 'numeric' });
  if (pathname === '/performance/breakdown') return 'Performance · Breakdown';
  if (pathname === '/performance/discipline') return 'Performance · Discipline';
  if (pathname.startsWith('/playbook/')) return `Playbook · ${decodeURIComponent(pathname.slice('/playbook/'.length))}`;
  if (pathname.startsWith('/research/')) return decodeURIComponent(pathname.slice('/research/'.length));
  return NAV.find((n) => n.to === pathname)?.label ?? 'Back';
}

/** Describe the current page as a place to come back to. */
export function fromLocation(loc: Location, overridePath?: string): From {
  const path = overridePath ?? `${loc.pathname}${loc.search}`;
  const url = new URL(path, 'http://x');
  return { path, label: labelFor(url.pathname), state: loc.state };
}

/** Link state for links that should offer a way back here. */
export function useBackState(overridePath?: string) {
  const loc = useLocation();
  return { from: fromLocation(loc, overridePath) };
}

/**
 * Where this page was opened from: the link's state, or else a `back` path in the URL (trade pages keep it
 * there, so a refreshed or shared link still knows its way back).
 */
export function useFrom(): From | null {
  const loc = useLocation();
  const state = loc.state as { from?: From } | null;
  if (state?.from) return state.from;
  const back = new URLSearchParams(loc.search).get('back');
  if (!back || !back.startsWith('/') || back.startsWith('//')) return null; // only paths in this app
  const url = new URL(back, 'http://x');
  return { path: back, label: labelFor(url.pathname) };
}

/**
 * A trade page link that remembers the list it was opened from (`in`) and where to go back to (`back`), so its
 * arrows can step through that list. `in` is `ids:1,2,3` for a short list shown in full, or `q:<query>` for the
 * Trades list (its filters and sort, worked out again on the server).
 */
/** One day's trades in time order, as a `q:` list for tradeHref. */
export const dayList = (date: string) => `q:from=${date}&to=${date}&sort=open_time&dir=asc`;

export function tradeHref(id: number, list?: string, back?: string) {
  const q = new URLSearchParams();
  if (list) q.set('in', list);
  if (back) q.set('back', back);
  const s = q.toString();
  return `/trades/${id}${s ? `?${s}` : ''}`;
}
